import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormHelperText
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useLoading } from "../../../../util/LoadingProvider";
import { useHistory } from "react-router";
import UploadImage from "../../UploadImage";
import apiHelper from "../../../../util/ApiHelper";
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Autocomplete from "@material-ui/lab/Autocomplete";

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      title: yup.string(),
    }),
    tc: yup.object().shape({
      title: yup.string(),
    }),
  }),
  url: yup
    .string()
    .nullable()
    .optional()
    .matches(RegExp(/http:\/\/|https:\/\//g), {
      message: "Missing http:// or https://",
      excludeEmptyString: true,
    }),
  logo: yup.string(),
  categoryIds: yup.array().of(yup.number()),
  status: yup.number(),
});

const defaultFormValues = {
  sequence: 0,
  localizations: {
    en: {
      title: "",
      url: "",
      description: null,
    },
    tc: {
      title: "",
      url: "",
      description: null,
    },
  },
  logo: "",
  status: 0,
  categoryIds: [],
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

export default function SponsorEditPage({ disabled, ...props }) {
  const { match } = props;
  const history = useHistory();
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const [sponsorCatList, setSponsorCatList] = useState([]);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const categoryTag = useWatch({
    control,
    name: "categoryIds",
  });

  const onSubmit = (value) => {
    console.log("value:", value);
    if (id !== "new") updateSponsor(value);
    else createSponsor(value);
  };

  const getSponsorCategories = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getSponsorsCategory(null,null,null,null,null,true);
      console.log(result);
      const resultList = result.data.content.map((item) => ({
        label: item.localizations.en.name,
        id: item.id,
      }));
      setSponsorCatList(resultList);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, reset, setLoading]);

  const getSponsor = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getSponsorById(id);
      result.data.categoryIds = result.data.categories.map((item) => (item.id));
      console.log(result);
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, reset, setLoading]);

  const updateSponsor = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateSponsorById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
        history.push(`/hkiff/sponsors`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createSponsor = async (value) => {
    try {
      const result = await apiHelper.hkiff.createSponsor(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("The sponsor is created.");
        history.push(`/hkiff/sponsors/${result.data.id}`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getSponsorCategories();
    if (id !== "new") getSponsor();
  }, [getSponsor, id]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Sponsor &gt; {id === "new" ? "Add Sponsor" : "Edit Sponsor"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={6} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid container item xs={12}>
              <Controller
                defaultValue={[]}
                name="categoryIds"
                control={control}
                render={({
                  field: { onChange, value, ...rest },
                  fieldState: { error },
                }) => {
                  return (
                    <Autocomplete
                      multiple
                      fullWidth
                      label="Category"
                      options={sponsorCatList}
                      disabled={disabled}
                      getOptionSelected={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          margin="dense"
                          placeholder="Category"
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                      value={sponsorCatList.filter((o) =>
                        categoryTag?.includes(o.id)
                      )}
                      onChange={(e, values) => {
                        console.log(values);
                        setValue(
                          "categoryIds",
                          values.map((item) => item.id)
                        );
                      }}
                      {...rest}
                    />
                  );
                }}
              />
            </Grid>


            {/* not yet implemented */}
            {/* <Grid item xs={12}>
            <Controller
              name="category"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Category"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            />
          </Grid> */}

            <Grid item xs={12}>
              {defaultValues && (
                <UploadImage
                  name="logo"
                  data={defaultValues}
                  control={control}
                  Controller={Controller}
                  setValue={setValue}
                  getValues={getValues}
                  disabled={disabled}
                  uploadLabel="Logo Image"
                  changeLabel="Logo Image"
                  uploadFolder="SPONSOR"
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="localizations.en.url"
                control={control}
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <TextField
                    label="URL"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...rest}
                    value={value}
                    onChange={(event) => {
                      onChange(event);
                      setValue("localizations.tc.url", event.target.value);
                    }}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "52px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value}
                    onChange={(event, newFormats) => {
                      onChange(newFormats);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value={1}
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value={0}
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Save Changes
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
